import styles from "./index.module.scss";
import React, { useState, MouseEvent, useEffect } from "react";
import classnames from "classnames";
import Star from "./Star";
import { nanoid } from "nanoid";

interface StarRatingProps {
  unit?: "full" | "half" | "float";
  size?: number;
  count?: number;
  innerRadius?: number;
  outerRadius?: number;
  activeColor?: string;
  hoverColor?: string;
  emptyColor?: string;
  roundedCorner?: boolean;
  handleOnClick?: (rating: number) => void;
  isReadOnly?: boolean;
  initialRating?: number;
  containerClassName?: string;
  starClassName?: string;
}

const DEFAULT_ACTIVE_COLOR = "#ffd055";
const DEFAULT_HOVER_COLOR = "#ffd055";
const starUnitMap = {
  full: 100,
  half: 50,
  float: 10,
};
const StarRating: React.FC<StarRatingProps> = ({
  size = 30,
  count = 5,
  innerRadius = 25,
  outerRadius = 50,
  activeColor = DEFAULT_ACTIVE_COLOR,
  hoverColor = DEFAULT_HOVER_COLOR,
  roundedCorner = true,
  handleOnClick,
  isReadOnly = false,
  initialRating = 0,
  starClassName,
  containerClassName,
  emptyColor = "#ddd",
  unit = "full",
}) => {
  const [hoverValue, setHoverValue] = useState(0);
  const [selectedValue, setSelectedValue] = useState(0);

  const unitValue = starUnitMap[unit];
  useEffect(() => {
    setSelectedValue(initialRating);
    setHoverValue(0);
  }, [initialRating]);

  const handleStarMouseMove = (offsetX: number, index: number) => {
    if (isReadOnly) return;
    setHoverValue(index + offsetX / size);
  };

  const handleMouseOut = () => {
    if (isReadOnly) return;
    setHoverValue(0);
  };

  const handleStarClick = (e: MouseEvent, index: number) => {
    if (isReadOnly) return;
    const value = e.nativeEvent.offsetX / size + index;
    setSelectedValue(e.nativeEvent.offsetX / size + index);
    if (handleOnClick) {
      if (unit === "full") {
        handleOnClick(Math.ceil(value));
      } else if (unit === "half") {
        handleOnClick(Math.ceil(value / 0.5) * 0.5);
      } else {
        handleOnClick(Math.ceil(value));
      }
    }
  };

  const getHoverOffsetPercent = (starIndex: number) => {
    const roundedValue = Math.floor(hoverValue);
    if (starIndex < roundedValue) {
      return 100;
    } else if (starIndex > roundedValue) {
      return 0;
    } else {
      if (roundedValue === 0) {
        return 0;
      } else {
        return 100;
      }
    }
  };

  const getSelectedOffsetPercent = (starIndex: number) => {
    const roundedSelectedValue = Math.floor(selectedValue);
    const roundedValue = Math.floor(hoverValue);
    if (roundedSelectedValue === 0) {
      return 0;
    }

    if (hoverValue < 1 && hoverValue > 0 && starIndex == 0) {
      return 100;
    }

    if (hoverValue < 1 && hoverValue > 0 && starIndex > 0) {
      return 0;
    }
    if (roundedValue !== 0 && starIndex > roundedValue) {
      return 0;
    }
    if (starIndex < roundedSelectedValue) {
      return 100;
    } else if (starIndex > roundedSelectedValue) {
      return 0;
    } else {
      const currentStarOffsetPercentage = (selectedValue % 1) * 100;
      return Math.ceil(currentStarOffsetPercentage / unitValue) * unitValue;
    }
  };
  return (
    <div className={classnames(containerClassName, styles.star)}>
      <span>Đánh giá bài viết:</span>
      {Array.from({ length: count }, (v, i) => {
        const isHover = getHoverOffsetPercent(i) > 0;
        const offset = isHover ? getHoverOffsetPercent(i) : getSelectedOffsetPercent(i);
        const filledColor = isHover ? hoverColor : activeColor;
        return (
          <Star
            key={i}
            index={i}
            size={size}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            filledColor={filledColor}
            emptyColor={emptyColor}
            handleStarMouseMove={handleStarMouseMove}
            handleMouseOut={handleMouseOut}
            handleStarClick={(e) => handleStarClick(e, i)}
            strokeLinejoin={roundedCorner ? "round" : "miter"}
            strokeLinecap={roundedCorner ? "round" : "butt"}
            className={starClassName}
            isReadOnly={isReadOnly}
            offset={offset}
          />
        );
      })}
    </div>
  );
};

export default StarRating;
